import React, { useState } from "react";
import { motion } from "framer-motion";
import { NavLink, useNavigate } from "react-router-dom";
import "./login.css";
import axios from "axios";

const API_BASE = "http://localhost:5000/api/admin";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ mobile: "", password: "" });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot Password States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotMobile, setForgotMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const validate = () => {
    const newErrors = {};
    if (!formData.mobile) newErrors.mobile = "Mobile number is required";
    if (!formData.password) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setServerError("");

    try {
      console.log("Attempting login with:", { mobile: formData.mobile });

      const res = await axios.post(`${API_BASE}/login`, {
        mobile: formData.mobile.trim(),
        password: formData.password
      });

      console.log("Login response:", res.data);

      const { token, admin } = res.data;

      if (!token) {
        throw new Error("No token received");
      }

      // Store admin data
      localStorage.setItem("adminToken", token);
      localStorage.setItem("adminData", JSON.stringify(admin));
      localStorage.setItem("role", "admin");

      console.log("Login successful, redirecting to dashboard");

      // Redirect to dashboard
      navigate("/admin/dashboard");

    } catch (error) {
      console.error("Login error:", error);
      console.error("Error response:", error.response);

      if (error.response) {
        // Server responded with error
        switch (error.response.status) {
          case 400:
            setServerError(error.response?.data?.message || "Invalid credentials");
            break;
          case 401:
            setServerError(error.response?.data?.message || "Session expired or account logged in elsewhere");
            break;
          case 404:
            setServerError("Admin not found. Please check your mobile number.");
            break;
          default:
            setServerError(error.response?.data?.message || "Login failed. Please try again.");
        }
      } else if (error.request) {
        // Request was made but no response
        setServerError("Cannot connect to server. Please check if backend is running.");
      } else {
        // Something else happened
        setServerError(error.message || "Login failed. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Forgot Password - Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!forgotMobile) {
      setForgotError("Please enter your mobile number");
      return;
    }

    setForgotLoading(true);
    setForgotError("");
    setForgotMessage("");

    try {
      const res = await axios.post(`${API_BASE}/forgot-password`, { mobile: forgotMobile });
      setForgotMessage(res.data.message);
      setForgotStep(2);
    } catch (error) {
      setForgotError(error.response?.data?.message || "Failed to send OTP");
    } finally {
      setForgotLoading(false);
    }
  };

  // Handle Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp) {
      setForgotError("Please enter the OTP");
      return;
    }

    setForgotLoading(true);
    setForgotError("");
    setForgotMessage("");

    try {
      const res = await axios.post(`${API_BASE}/verify-otp`, {
        mobile: forgotMobile,
        otp: otp
      });
      setResetToken(res.data.resetToken);
      setForgotMessage("OTP verified successfully!");
      setForgotStep(3);
    } catch (error) {
      setForgotError(error.response?.data?.message || "Invalid OTP");
    } finally {
      setForgotLoading(false);
    }
  };

  // Handle Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      setForgotError("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setForgotError("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setForgotError("Password must be at least 6 characters");
      return;
    }

    setForgotLoading(true);
    setForgotError("");
    setForgotMessage("");

    try {
      await axios.post(`${API_BASE}/reset-password`, {
        resetToken,
        newPassword
      });

      setForgotMessage("Password reset successfully! Please login with your new password.");

      setTimeout(() => {
        setShowForgotPassword(false);
        resetForgotState();
      }, 2000);

    } catch (error) {
      setForgotError(error.response?.data?.message || "Failed to reset password");
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotState = () => {
    setShowForgotPassword(false);
    setForgotMobile("");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setResetToken("");
    setForgotStep(1);
    setForgotMessage("");
    setForgotError("");
    setForgotLoading(false);
  };

  return (
    <div>
      <div className="pro-login-wrapper d-flex align-items-center justify-content-center">
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="pro-login-card p-5 rounded-4 shadow-sm"
          style={{ maxWidth: "450px", width: "100%" }}
        >
          <h3 className="text-center mb-4 fw-bold">Tolvv Admin Login</h3>

          {serverError && (
            <div className="alert alert-danger text-center" role="alert">
              {serverError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label fw-semibold">Mobile Number</label>
              <input
                type="text"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                className="form-control pro-input"
                placeholder="Enter mobile number"
                autoComplete="off"
              />
              {errors.mobile && <div className="text-danger mt-1">{errors.mobile}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-control pro-input"
                placeholder="Enter password"
              />
              {errors.password && <div className="text-danger mt-1">{errors.password}</div>}
            </div>

            <div className="text-end mb-3">
              <button
                type="button"
                className="btn btn-link text-dark p-0 text-decoration-none"
                onClick={() => setShowForgotPassword(true)}
                style={{ fontSize: "14px" }}
              >
                Forgot Password?
              </button>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="btn w-100 py-2 btn-dark"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Signing In...
                </>
              ) : (
                "Admin Sign In"
              )}
            </motion.button>

            <p className="text-center mt-3 small">
              Not admin?
              <NavLink to="/" className="pro-link ms-1 text-dark">
                User Login
              </NavLink>
            </p>
          </form>
        </motion.div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reset Password</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={resetForgotState}
                ></button>
              </div>
              <div className="modal-body">
                {forgotMessage && (
                  <div className="alert alert-success">{forgotMessage}</div>
                )}
                {forgotError && (
                  <div className="alert alert-danger">{forgotError}</div>
                )}

 

                {forgotStep === 1 && (
                  <form onSubmit={handleSendOTP}>
                    <div className="mb-3">
                      <label className="form-label">Mobile Number</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={forgotMobile}
                        onChange={(e) => setForgotMobile(e.target.value)}
                        placeholder="Enter your registered mobile number"
                        required
                      />
                      <small className="text-muted">
                        We'll send a 6-digit OTP to this mobile number
                      </small>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-dark w-100"
                      disabled={forgotLoading}
                    >
                      {forgotLoading ? "Sending..." : "Send OTP"}
                    </button>
                  </form>
                )}

                {forgotStep === 2 && (
                  <form onSubmit={handleVerifyOTP}>
                    <div className="mb-3">
                      <label className="form-label">Enter OTP</label>
                      <input
                        type="text"
                        className="form-control"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        placeholder="Enter 6-digit OTP"
                        maxLength="6"
                        required
                      />
                      <small className="text-muted">
                        OTP sent to {forgotMobile}
                      </small>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-dark w-100"
                      disabled={forgotLoading}
                    >
                      {forgotLoading ? "Verifying..." : "Verify OTP"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-link w-100 mt-2"
                      onClick={() => setForgotStep(1)}
                    >
                      Back to Email
                    </button>
                  </form>
                )}

                {forgotStep === 3 && (
                  <form onSubmit={handleResetPassword}>
                    <div className="mb-3">
                      <label className="form-label">New Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Confirm Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      className="btn btn-dark w-100"
                      disabled={forgotLoading}
                    >
                      {forgotLoading ? "Resetting..." : "Reset Password"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLogin;